<template>
    <BaseModal :title="'Add New Administrator'" @close="closeModal" :visible="true">
        <form @submit.prevent="onSubmit">
            <div class="rlms-flex rlms-flex-col rlms-gap-30">
                <div class="pp-body-scroll">
                    <div class="rlms-row rlms-row-responsive">
                        <div class="rlms-col-1">
                            <label for="email">Email address</label>
                            <input type="text" placeholder="Email address"
                                v-model.trim="email" @input="email = $event.target.value.toLowerCase()" />
                            <span class="rlms-text-danger-500 rlms-fs-xxs error-text"
                                v-if="submitted && emailError">{{ emailError }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </form>
        <template #footer>
            <div class="pp-footer rlms-flex rlms-justify-between" style="padding: 0;">
                <button class="rlms-btn rlms-btn-outline-primary p-cancel-btn" type="button" id="cancel-org-modal"
                    @click="closeModal">Cancel</button>
                <button type="submit" class="rlms-btn rlms-btn-primary" id="save-org-btn"
                    @click="onSubmit">Save</button>
            </div>
        </template>
    </BaseModal>
</template>

<script setup>
import { ref } from 'vue';
import BaseModal from './BaseModal.vue';
import { useForm, useField } from 'vee-validate';
import * as yup from 'yup';
import axios from '@/services/axios';
import { handleError } from '@/utils/handleError';
import { useLoaderStore } from '@/stores/loader';
import { showToast } from '@/utils/swal';

const loader = useLoaderStore();

// eslint-disable-next-line
const emit = defineEmits(['close', 'saved']);

// eslint-disable-next-line
const props = defineProps({
    organizationId: {
        type: Number,
        required: true,
    },
});

if (!props.organizationId || typeof props.organizationId !== 'number') {
    throw new Error('orgId prop is required');
}

const showModal = ref(true);
const submitting = ref(false);
const submitted = ref(false);

const closeModal = () => {
    showModal.value = false;
    emit('close');
};

// Replace this with props or API fetched user
const formSchema = yup.object({
    email: yup.string().email('Please enter a valid email.').required('Please enter administrator email.').test(
        'has-dot-in-domain',
        'Please enter a valid email.',
        (value) => {
            if (!value) return false;
            const domain = value.split('@')[1];
            return domain && domain.includes('.');
        }
    ),
});

// Setup form with schema
const { handleSubmit, setErrors } = useForm({
    validationSchema: formSchema,
})

// Fields
const { value: email, errorMessage: emailError } = useField('email');

// Submit handler
const onSubmit = async () => {
    submitted.value = true;

    await handleSubmit(async (values) => {
        submitting.value = true;

        try {
            loader.show();

            await axios.post(`/organizations/${props.organizationId}/administrators`, { ...values });

            showToast({
                icon: 'success',
                title: 'Great! ',
                text: 'Administrator added successfully!',
            });

            emit('saved');
        } catch (error) {
            if (error?.response?.status === 422) {
                const serverErrors = error?.response?.data?.errors || {};
                const fieldErrors = {};

                Object.keys(serverErrors).forEach(key => {
                    fieldErrors[key] = serverErrors[key][0];
                });

                if (Object.keys(fieldErrors).length > 0) {
                    setErrors(fieldErrors);
                } else {
                    handleError(error, true);
                }
            } else {
                handleError(error);
            }
        } finally {
            submitting.value = false;
            loader.hide();
        }
    })();
};
</script>
